
import React from 'react';
import { ConnectionStatus, Language } from '../types';
import { t } from '../locales';

interface MicButtonProps {
  status: ConnectionStatus;
  onClick: () => void;
  volume: number; // 0 to 1
  language: Language;
}

const MicButton: React.FC<MicButtonProps> = ({ status, onClick, volume, language }) => {
  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';

  // Calculate pulse effect based on volume when connected
  const scale = isConnected ? 1 + Math.min(volume, 0.5) : 1;
  const shadowSize = isConnected ? Math.min(volume * 20, 15) + 5 : 0;

  return (
    <div className="fixed bottom-8 left-0 right-0 flex flex-col justify-end items-center z-50 pointer-events-none gap-4">
      {/* Visual Instruction Label when recording */}
      {isConnected && (
        <div className="bg-black/70 backdrop-blur-sm text-white px-4 py-2 rounded-full text-xs font-bold animate-bounce shadow-lg pointer-events-auto cursor-pointer" onClick={onClick}>
          {t(language, 'clickToStop')}
        </div>
      )}

      <button
        id="mic-button"
        onClick={onClick}
        disabled={isConnecting}
        className={`
          pointer-events-auto
          relative
          w-20 h-20
          rounded-full
          flex items-center justify-center
          shadow-lg
          transition-all duration-200 ease-in-out
          ${isConnecting ? 'bg-gray-400 cursor-wait' : ''}
          ${isConnected ? 'bg-red-500 hover:bg-red-600' : 'bg-emerald-600 hover:bg-emerald-500'}
          text-white
        `}
        style={{
          transform: `scale(${scale})`,
          boxShadow: isConnected 
            ? `0 0 0 ${shadowSize}px rgba(239, 68, 68, 0.3), 0 10px 15px -3px rgba(0, 0, 0, 0.1)`
            : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}
      >
        {isConnecting ? (
          <svg className="animate-spin h-8 w-8 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        ) : isConnected ? (
          // Stop/Waveform Icon when recording
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="6" width="12" height="12" rx="2" />
          </svg>
        ) : (
          // PLUS Icon for "New Meal"
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
          </svg>
        )}
        
        {/* Ripple effect rings when connected */}
        {isConnected && (
          <>
             <div className="absolute inset-0 rounded-full border-2 border-red-400 animate-ping opacity-75"></div>
          </>
        )}
      </button>
      
      {/* New Meal Tooltip (only when NOT connected) */}
      {!isConnected && !isConnecting && (
        <div className="absolute bottom-24 bg-white px-4 py-2 rounded-lg shadow-md text-sm text-gray-600 whitespace-nowrap animate-bounce">
          Add new meal
        </div>
      )}
    </div>
  );
};

export default MicButton;
